// The catch-all behind /submission/flow/step/:step.
//
// It used to BE step 4 — a dashed placeholder saying the trial and review
// screens were coming. Steps 1-4 all have their own routes now, so the only
// thing that reaches here is an out-of-range step number: a hand-typed URL, a
// stale link from a document, or a fat-fingered demo. Rendering the old stub
// then meant showing a screen that no longer exists and telling the visitor the
// flow was unfinished when it is not.
//
// So it resolves instead of renders: restore the tab's flow, re-check the
// deadline gate, and hand the visitor to the step they are actually on.

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import { fallbackTo, hasFlowContext } from "../common/flowGuard";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { parseSubmissionParams } from "../common/submissionParams";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
  match: any;
}

const SubmissionWizardShell: React.FC<Props> = ({
  history,
  location,
  breakdownId,
  entrySource,
  refreshGate,
  rehydrate,
}) => {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const boot = async () => {
      let hasFlow = !!entrySource;
      if (!hasFlow) {
        // Refresh/deep link: try to restore the tab's flow (re-check point 3).
        hasFlow = await rehydrate();
      }
      if (!hasFlow || !hasFlowContext({ search: location.search, breakdownId })) {
        // No breakdown to submit to — traditional signup.
        history.replace(fallbackTo(location.search));
        return;
      }

      // Gate re-check point 2 — navigates to /submission/closed itself when
      // the breakdown expired or the role was cast while we were away. Do it
      // before forwarding, so a closed job is not answered with a step.
      const info = await refreshGate({ history, search: location.search });
      if (!info) {
        // refreshGate either navigated or could not reach the server; either
        // way this page has nothing useful to say.
        setStuck(true);
        return;
      }

      // Where the visitor actually is. The account is created at email
      // verification, so having a token is exactly the line between "still
      // signing up" and "has a submission in progress".
      const params = parseSubmissionParams(location.search);
      const search = location.search || (params.bd ? `?bd=${params.bd}` : "");
      history.replace(
        isSubmissionAuthed()
          ? `/submission/flow/step/4${search}`
          : `/submission/flow/step/1${search}`
      );
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SubmissionLayout>
      <Centered aria-busy={!stuck}>
        <Message>
          {stuck
            ? i18next.t("We couldn't pick up where you left off.")
            : i18next.t("One moment…")}
        </Message>
        {stuck && (
          <TextButton
            type="button"
            onClick={() => history.replace("/submission/casting-calls")}
          >
            {i18next.t("Browse casting calls")}
          </TextButton>
        )}
      </Centered>
    </SubmissionLayout>
  );
};

const Centered = styled.div`
  text-align: center;
  padding: 48px 0;
`;

const Message = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #122640;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 0;
  cursor: pointer;
  min-height: 44px;
`;

interface StateProps {
  breakdownId: string | null;
  entrySource: string | null;
}

interface DispatchProps {
  refreshGate: (payload: { history: any; search?: string }) => Promise<any>;
  rehydrate: () => Promise<boolean>;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  breakdownId: state.submissionFlowModel.breakdownId,
  entrySource: state.submissionFlowModel.entrySource,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  refreshGate: dispatch.submissionFlowModel.refreshGate,
  rehydrate: dispatch.submissionFlowModel.rehydrate,
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionWizardShell);
